import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isValidDateUK, formatDateUK } from '../utils/dateUtils';
import styles from './DateInput.module.css';

/**
 * DateInput component using react-datepicker
 * Handles DD/MM/YYYY format internally while providing a proper date picker UI
 */
const DateInput = ({
  id,
  name,
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "",
  error = null,
  required = false,
  disabled = false,
  minDate = null,
  maxDate = null
}) => {

  /**
   * Convert DD/MM/YYYY string to Date object
   */
  const parseUKDate = (dateStr) => {
    if (!dateStr || dateStr === 'Invalid Date') return null;

    // Handle YYYY-MM-DD format (ISO)
    if (dateStr.includes('-') && dateStr.length === 10) {
      const [year, month, day] = dateStr.split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }

    // Handle DD/MM/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts.map(Number);
        if (day && month && year) {
          return new Date(year, month - 1, day);
        }
      }
    }

    return null;
  };

  /**
   * Convert Date object to DD/MM/YYYY string
   */
  const formatToUK = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const selectedDate = parseUKDate(value);

  const handleChange = (date) => {
    const ukDateStr = formatToUK(date);

    // Create synthetic event to match expected onChange signature
    const syntheticEvent = {
      target: {
        name,
        value: ukDateStr
      }
    };
    onChange(syntheticEvent);
  };

  const inputClassName = `
    ${styles.dateInput}
    ${className}
    ${error ? styles.error : ''}
  `.trim();

  return (
    <div className={styles.dateInputContainer}>
      <DatePicker
        id={id}
        name={name}
        selected={selectedDate}
        onChange={handleChange}
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder}
        className={inputClassName}
        required={required}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        autoComplete="off"
        isClearable={!required}
      />
      {error && (
        <span className={styles.errorText}>
          {error}
        </span>
      )}
    </div>
  );
};

export default DateInput;
